import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DsNotificationService } from '@skyeng/libs/shared/ui-kit-v2/notification';
import { IVimCoreAvatarUser, VimCoreCurrentUserService } from '@skyeng/libs/vimbox-core/user';
import { AttachmentStatus, MAX_FILE_SIZE_MB, MAX_FILES_COUNT } from '../../const';
import { Attachment, AttachmentUpload, MessageEditContent, MessageRequest } from '../../interface';
import { MessageRequestService } from '../../service/message-request.service';
import { TranslocoService } from '@ngneat/transloco';
import { DecorateUntilDestroy, filterEmpty, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { fromEvent, filter, map } from 'rxjs';
import loader1 from './images/loader.svg';
import deleteAttachment from './images/delete-attachment.svg';

@DecorateUntilDestroy()
@Component({
  selector: 'skypro-message-request',
  templateUrl: 'message-request.html',
  styleUrls: ['message-request.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [MessageRequestService],
})
export class MessageRequestComponent implements OnInit, AfterViewInit {
  @Input() placeholder: string;
  @Input() editMessageContent?: MessageEditContent;
  @Input() isLoading = false;
  @Input() scrollingContainer: string | null = null;
  // Включает функцию сохранения не отправленного текста в LS
  @Input() needSaveInProgressText = true;
  // Кастомный ключ локал стораджа для сохранения сообщений
  @Input() localStorageKey: string | null = null;
  @Input() validationError: string | null = null;
  // Отключает кнопку сабмита, если текста сообщения нет
  @Input() submitButtonDisabled = false;
  @Input() submitButtonCaption?: string;
  @Input() showSubmitButton = true;
  @Input() allowAttachments = true;
  @Input() showAuthorData = true;
  @Input() isPlainTextMode = false;
  @Input() isMessengerMode = false;
  @Input() hasAutofocus = false;
  @Input() allowedAttachmentTypes: string[] | null = null;

  readonly isTextareaDisabled = input(false);

  @Output() submit = new EventEmitter<MessageRequest>();
  @Output() cancelEditing = new EventEmitter<void>();
  @Output() messageChanged = new EventEmitter<void>();

  @ViewChild('messengerInput') messengerInput!: ElementRef<HTMLInputElement>;

  private dsNotificationService = inject(DsNotificationService);
  private messageRequestService = inject(MessageRequestService);
  private translocoService = inject(TranslocoService);
  private vimCoreCurrentUserService = inject(VimCoreCurrentUserService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  fileOver: boolean;

  icons = {
    loader: loader1,
    close: deleteAttachment,
  };

  attachmentsUpload$ = this.messageRequestService.attachmentsUpload$;
  progress$ = this.messageRequestService.progress$;
  message$ = this.messageRequestService.message$;

  currentUser$ = this.vimCoreCurrentUserService.authorizedUser$.pipe(filterEmpty());
  currentUserName$ = this.currentUser$.pipe(map(user => this.getAuthorName(user)));
  currentUserAvatarUrl$ = this.currentUser$.pipe(map(({ avatarUrl }) => avatarUrl));

  attachmentStatus = AttachmentStatus;

  // Пределы студент должен видеть до выбора файла, а не узнавать из отказа.
  readonly attachmentLimits = { MAX_FILE_SIZE_MB, MAX_FILES_COUNT };

  ngOnInit(): void {
    this.messageRequestService.initMessage(this.needSaveInProgressText, this.localStorageKey);
  }

  ngAfterViewInit(): void {
    if (this.editMessageContent?.body) {
      this.setMessage(this.editMessageContent.body);

      if (this.allowAttachments) {
        this.setAttachments(this.editMessageContent.attachments);
      }

      // Без этого в режиме isMessengerMode после второго редактирования не подставляется сообщение
      this.changeDetectorRef.detectChanges();
    }

    if (this.isMessengerMode && this.messengerInput) {
      // В режиме мессенджера можно отправить сообщение по Enter, если отправка не заблокирована
      fromEvent<KeyboardEvent>(this.messengerInput.nativeElement, 'keydown')
        .pipe(
          filter(event => {
            if (event.key !== 'Enter' || event.shiftKey) {
              return false;
            }

            return true;
          }),
          takeUntilDestroyed(this),
        )
        .subscribe(event => {
          // Перенос строчки мы в любом случае не делаем
          event.preventDefault();
          if (!this.canSubmit()) {
            return;
          }

          this.submitMessage();
        });
    }
  }

  @HostListener('window:dragover', ['$event'])
  onDragOverWindow(event: DragEvent): void {
    event.preventDefault();
  }

  @HostListener('window:drop', ['$event'])
  onDropWindow(event: DragEvent): void {
    event.preventDefault();
  }

  canSubmit(): boolean {
    const message = this.getMessage();

    if (!message) {
      return false;
    }

    if (message.text.length === 0 && message.attachments.length === 0) {
      return false;
    }

    if (this.isLoading || this.submitButtonDisabled || this.isTextareaDisabled()) {
      return false;
    }

    return true;
  }

  trackByAttachment(_index: number, attachment: AttachmentUpload): string {
    return attachment.uuid;
  }

  submitMessage(): void {
    const isAttachmentsValid = this.getAttachmentsValidState();
    if (isAttachmentsValid === false) {
      return;
    }

    const message = this.getMessage();
    this.submit.emit(message);
  }

  /**
   * !! Метод вызывается родительским компонентом после отправки сообщения
   */
  clear(): void {
    this.messageRequestService.clear();
  }

  getMessage(): MessageRequest {
    return {
      text: this.messageRequestService.getMessage(),
      attachments: this.messageRequestService.getAttachments().map(({ url, title }) => ({ url, title })),
    };
  }

  // Метод дергается наружу по аналогии с clear
  setMessage(message: string): void {
    this.messageRequestService.setMessage(message, this.needSaveInProgressText, this.localStorageKey);
  }

  getAttachmentsValidState(): boolean {
    const uploadingAttachmets = this.messageRequestService
      .getAttachmentsUpload()
      .filter(({ status }) => status === AttachmentStatus.uploading);

    const errorAttachmets = this.messageRequestService
      .getAttachmentsUpload()
      .filter(({ status }) => status === AttachmentStatus.error);

    if (uploadingAttachmets.length > 0) {
      this.dsNotificationService.openErrorNotification({
        content: this.translocoService.translate('skyproLibMessages.waitUntilFilesUploaded'),
        autoClosing: true,
      });
      return false;
    }

    if (errorAttachmets.length > 0) {
      this.dsNotificationService.openErrorNotification({
        content: this.translocoService.translate('skyproLibMessages.fewFilesUploadFailed'),
        autoClosing: true,
      });
      return false;
    }

    return true;
  }

  onMessageChange(message: string): void {
    this.setMessage(message);
    this.messageChanged.emit();
  }

  onTextareaChange(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.onMessageChange(textarea.value);
  }

  setAttachments(attachments: Attachment[]): void {
    this.messageRequestService.setAttachments(attachments);
  }

  onSelectFile(event: Event): void {
    const target = event.target! as HTMLInputElement;

    if (!target.files?.length) {
      return;
    }

    const files = Array.from(target.files);
    this.validateAndAddAttachments(files);

    // eslint-disable-next-line functional/immutable-data
    target.value = '';
  }

  onFileDropped(event: DragEvent): void {
    const files = Array.from((event.dataTransfer?.files as FileList) ?? []);
    this.validateAndAddAttachments(files);
  }

  private validateAndAddAttachments(files: File[]): void {
    const validFiles = this.allowedAttachmentTypes
      ? this.messageRequestService.getValidFiles(files, this.allowedAttachmentTypes)
      : [...files];

    this.messageRequestService.addAttachments(Array.from(validFiles));
  }

  onDeleteAttachment(uuid: string): void {
    this.messageRequestService.removeAttachment(uuid);
  }

  private getAuthorName(user: IVimCoreAvatarUser | undefined): string {
    return user?.name && user?.surname ? `${user?.name} ${user?.surname}` : `${user?.name || user?.surname}`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.allowAttachments) {
      return;
    }

    if (this.fileOver) {
      return;
    }

    this.fileOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.allowAttachments) {
      return;
    }

    this.fileOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.allowAttachments) {
      return;
    }

    this.fileOver = false;
    this.onFileDropped(event);
  }
}
